import { Confidence } from '../types/detection.js';
import { classifyConfidence } from './classifyConfidence.js';
import ambiguousMapData from '../data/ambiguousAllergenMap.json' with { type: 'json' };
// Single source of truth for ambiguous terms (exported for reference resources)
export const ambiguousTerms = ambiguousMapData.ambiguousTerms.map((e) => e.term);
export function detectAllergens(tokens, library, mode) {
    const detections = [];
    let detectionCounter = 0;
    // Filter library to current regulatory mode
    const isEU = mode === 'EU' || mode === 'EU_NON_PREPACKED';
    const filteredLibrary = library.filter((entry) => isEU ? entry.isEU : entry.isUS);
    // Build token lookup for parent context resolution
    const tokenMap = new Map();
    for (const t of tokens) {
        tokenMap.set(t.id, t);
    }
    // Build lookup: normalized term → allergen entry
    const lookup = new Map();
    for (const entry of filteredLibrary) {
        lookup.set(entry.canonicalName.toLowerCase().trim(), entry);
        for (const synonym of entry.synonyms) {
            const key = synonym.toLowerCase().trim();
            if (!lookup.has(key)) {
                lookup.set(key, entry);
            }
        }
    }
    const triggerLabel = (token) => {
        if (token.parentId) {
            const parent = tokenMap.get(token.parentId);
            if (parent) {
                const parentName = parent.rawText.replace(/\s*\([^)]*\)\s*/g, '').trim();
                if (parentName)
                    return `${parentName} (${token.name})`;
            }
        }
        return token.name;
    };
    for (const token of tokens) {
        const normalizedName = token.name.toLowerCase().trim();
        if (!normalizedName)
            continue;
        // 1. Exact match
        const exactMatch = lookup.get(normalizedName);
        if (exactMatch) {
            detections.push({
                id: `det-${detectionCounter++}`,
                ingredientId: token.id,
                allergenId: exactMatch.id,
                allergenCategory: exactMatch.category,
                confidence: Confidence.Present,
                matchedTerm: normalizedName,
                matchType: 'exact',
                species: exactMatch.species ?? undefined,
                triggeringIngredients: [triggerLabel(token)],
                evidenceChain: [{ matchedTerm: normalizedName, matchType: 'exact', source: 'library' }],
            });
            continue;
        }
        // 2. Synonym match (check if any synonym is a substring of the token name or vice versa)
        let found = false;
        for (const entry of filteredLibrary) {
            for (const synonym of entry.synonyms) {
                const normSyn = synonym.toLowerCase().trim();
                if (normSyn === normalizedName) {
                    detections.push({
                        id: `det-${detectionCounter++}`,
                        ingredientId: token.id,
                        allergenId: entry.id,
                        allergenCategory: entry.category,
                        confidence: Confidence.Present,
                        matchedTerm: synonym,
                        matchType: 'synonym',
                        species: entry.species ?? undefined,
                        triggeringIngredients: [triggerLabel(token)],
                        evidenceChain: [{ matchedTerm: synonym, matchType: 'synonym', source: 'synonyms' }],
                    });
                    found = true;
                    break;
                }
            }
            if (found)
                break;
        }
        if (found)
            continue;
        // 3. Partial/derived match — check if name contains any allergen term
        let bestMatch = null;
        for (const entry of filteredLibrary) {
            const termsToCheck = [entry.canonicalName, ...entry.synonyms];
            for (const term of termsToCheck) {
                const normTerm = term.toLowerCase().trim();
                if (normTerm.length < 3)
                    continue; // Skip very short terms
                if (normalizedName.includes(normTerm) || normTerm.includes(normalizedName)) {
                    const shorter = Math.min(normalizedName.length, normTerm.length);
                    const longer = Math.max(normalizedName.length, normTerm.length);
                    if (shorter / longer < 0.5)
                        continue;
                    const similarity = computeSimilarity(normalizedName, normTerm);
                    if (!bestMatch || similarity > bestMatch.similarity) {
                        bestMatch = { entry, term, similarity };
                    }
                }
            }
        }
        if (bestMatch) {
            const confidence = classifyConfidence('derived', bestMatch.similarity);
            detections.push({
                id: `det-${detectionCounter++}`,
                ingredientId: token.id,
                allergenId: bestMatch.entry.id,
                allergenCategory: bestMatch.entry.category,
                confidence,
                matchedTerm: bestMatch.term,
                matchType: 'derived',
                species: bestMatch.entry.species ?? undefined,
                triggeringIngredients: [triggerLabel(token)],
                evidenceChain: [{ matchedTerm: bestMatch.term, matchType: 'derived', source: 'library' }],
            });
        }
        // "natural flavor" → NeedsReview (terms sourced from ambiguousAllergenMap.json)
        for (const ambiguous of ambiguousTerms) {
            if (normalizedName.includes(ambiguous)) {
                // Check if we already have a detection for this token
                const alreadyDetected = detections.some((d) => d.ingredientId === token.id);
                if (!alreadyDetected) {
                    detections.push({
                        id: `det-${detectionCounter++}`,
                        ingredientId: token.id,
                        allergenId: 'review-needed',
                        allergenCategory: 'unknown',
                        confidence: Confidence.NeedsReview,
                        matchedTerm: ambiguous,
                        matchType: 'partial',
                        triggeringIngredients: [triggerLabel(token)],
                        evidenceChain: [{ matchedTerm: ambiguous, matchType: 'partial', source: 'ambiguous-terms' }],
                    });
                }
                break;
            }
        }
    }
    return detections;
}
function computeSimilarity(a, b) {
    if (a === b)
        return 1;
    const longer = a.length > b.length ? a : b;
    const shorter = a.length > b.length ? b : a;
    if (longer.includes(shorter))
        return shorter.length / longer.length;
    // Simple overlap ratio
    let common = 0;
    for (let i = 0; i < shorter.length; i++) {
        if (longer.includes(shorter[i]))
            common++;
    }
    return common / longer.length;
}
//# sourceMappingURL=detectAllergens.js.map